// Runs a hub's golden question set as test runs and compares each outcome with
// the one it expects. See Design > Toolsets and tools, "Golden question sets",
// and Design > Exchange, "Test runs".

import { useState } from "preact/hooks";
import { Button, DataTable, DetailLayout, EmptyState, FigureStrip, fmtNum, Notice, PageHeader, PillGroup, RailPanel, StatusBadge } from "../../../../ui/src/index.ts";
import type { TableColumn } from "../../../../ui/src/index.ts";
import { describeFailure } from "../../api.ts";
import { outcomeKey, shortName } from "../../domain/activity.ts";
import { asExpected, goldenSet, TEST_RUN_GAP_MS, type GoldenResult } from "../../domain/golden.ts";
import { seconds } from "../../domain/money.ts";
import type { GoldenQuestion, HubSummary } from "../../domain/types.ts";
import { loadGolden, runGolden, stopGoldenRun } from "../actions.ts";
import { HeaderActions } from "../Guide.tsx";
import { Failed, Loading, Section } from "../parts.tsx";
import { useStore, type GoldenRun } from "../store.ts";
import { BADGE_TONES, OUTCOME_WORDS } from "../words.ts";
import { hubLabel, useGolden, useHubLabel } from "../hubData.ts";

type Toolsets = "active" | "staged";
type Row = { index: number; golden: GoldenQuestion; result?: GoldenResult };

const TOOLSET_OPTIONS = [
  { value: "active", label: "Active toolset" },
  { value: "staged", label: "Staged toolset", title: "The hub's newest staged toolset that has never been active, when it's newer than the active one" },
] as const;
const STATUS_WORDS: Record<GoldenRun["status"], string> = { running: "Running", stopped: "Stopped", done: "Finished", failed: "Stopped by an error" };

function Outcome({ outcome }: { outcome: string | null }) {
  if (outcome === null) return <span class="st-note">No hub responded</span>;
  const k = outcomeKey(outcome);
  return k ? <StatusBadge tone={BADGE_TONES[k]}>{OUTCOME_WORDS[k]}</StatusBadge> : <code>{outcome}</code>;
}

/** Where a response came from: the question (intent), and the hub when it isn't this one. */
function Where({ hub, intent, own }: { hub: string | null; intent: string | null; own: string }) {
  const parts = [hub && hub !== own ? shortName(hub) : null, intent].filter(Boolean);
  return parts.length ? <span class="st-note st-id">{parts.join(" · ")}</span> : null;
}

export function Golden({ hub }: { hub: HubSummary }) {
  const short = useHubLabel(hub.hub.name);
  const { golden } = useGolden(hub);
  const run = useStore((s) => s.goldenRun);
  const hubs = useStore((s) => s.hubs);
  const [toolsets, setToolsets] = useState<Toolsets>("active");
  const questions = golden?.status === "ok" ? goldenSet(golden.value.questions, hub.hub.name) : [];
  const mine = run?.hub.id === hub.hub.id ? run : null;
  const running = run?.status === "running";

  const rows: Row[] = questions.map((g, index) => {
    const result = mine?.results[index];
    return { index, golden: g, ...(result ? { result } : {}) };
  });
  const done = mine?.results.length ?? 0;
  const passed = mine?.results.filter(asExpected).length ?? 0;
  const left = mine && running ? Math.ceil(((mine.total - done) * TEST_RUN_GAP_MS) / 1000) : 0;

  const columns: TableColumn<Row>[] = [
    { id: "question", label: "Golden question", fill: true, resizable: true, render: (r) => <span class="st-ask st-cell-text" title={r.golden.question}>{r.golden.question}</span> },
    { id: "expected", label: "Expected", render: (r) => <div class="st-stack"><Outcome outcome={r.golden.expectOutcome} />{r.golden.expectIntent && <span class="st-note st-id">{r.golden.expectIntent}</span>}</div> },
    { id: "actual", label: "Got", render: (r) => (r.result ? <div class="st-stack"><Outcome outcome={r.result.outcome} /><Where hub={r.result.hub} intent={r.result.intent} own={hub.hub.name} />{r.result.detail && <span class="st-note st-detail">{r.result.detail}</span>}</div> : <span class="st-note">–</span>) },
    { id: "check", label: "Check", render: (r) => (r.result ? <StatusBadge tone={asExpected(r.result) ? "success" : "danger"}>{asExpected(r.result) ? "As expected" : "Not as expected"}</StatusBadge> : <span class="st-note">–</span>) },
    { id: "time", label: "Time", numeric: true, render: (r) => (r.result ? seconds(r.result.ms) : "–") },
  ];

  const start = () => void runGolden(hub.hub, questions, toolsets === "staged");
  const action = running
    ? <Button size="sm" onClick={stopGoldenRun}>Stop</Button>
    : <Button size="sm" variant="solid" disabled={questions.length === 0} onClick={start}>{mine ? "Run again" : "Run the golden questions"}</Button>;

  let body;
  if (!golden || golden.status === "loading") body = <Loading label="Loading golden questions" />;
  else if (golden.status === "failed") body = <Failed what="the golden questions" error={golden.error} retry={() => void loadGolden(true)} />;
  else if (questions.length === 0) body = <EmptyState>{short} has no golden questions yet. They live in the hub's toolset.</EmptyState>;
  else body = <>
    {run && running && !mine && <Notice title={`Studio is running ${hubLabel(run.hub.name, hubs)}'s golden questions`} action={<Button size="sm" onClick={stopGoldenRun}>Stop</Button>}>Stop that run to start this one.</Notice>}
    {mine?.status === "failed" && mine.error && <Notice live tone="danger" title="The run stopped">{describeFailure(mine.error)}</Notice>}
    {mine && <p class="st-note" role="status">{STATUS_WORDS[mine.status]}: {fmtNum(done)} of {fmtNum(mine.total)} asked{left > 0 ? `, about ${left} seconds left` : ""}.</p>}
    {mine && <progress class="st-progress" max={mine.total} value={done} aria-label="Golden questions asked" />}
    <DataTable caption={`Golden questions for ${short}`} columns={columns} rows={rows} rowKey={(r) => String(r.index)} storageKey="studio-golden-run" />
  </>;

  return <>
    <PageHeader title={<>Test the <strong>golden questions</strong></>}
      description={`Ask ${short} its own golden questions as test runs, one every ${TEST_RUN_GAP_MS / 1000} s, and compare what each one got with what it expects.`}
      action={<HeaderActions>{running ? null : <PillGroup label="Toolset to test" options={TOOLSET_OPTIONS} value={toolsets} onChange={setToolsets} />}{action}</HeaderActions>} />
    <DetailLayout rail={<>
      <RailPanel title="Test run">
        <p>{mine ? `This run used the ${mine.staged ? "staged" : "active"} toolset.` : `Studio will use the ${toolsets} toolset.`}</p>
        <p class="st-rail-note">Each question goes to this hub alone. With the staged toolset, the hub answers with its newest staged toolset that has never been active, when that's newer than the active one. Otherwise it answers with the active one.</p>
        <p class="st-rail-note">Test runs charge nothing and stay out of statistics and demand. Studio doesn't keep the results: reloading clears them.</p>
      </RailPanel>
    </>}>
      <div class="st-summary"><FigureStrip label="Golden question results" figures={[
        { label: "As expected", value: mine ? `${fmtNum(passed)} of ${fmtNum(done)}` : "–", context: mine ? `${fmtNum(done - passed)} not as expected` : "Run the golden questions to see" },
        { label: "Golden questions", value: fmtNum(questions.length), context: "From the hub's toolset" },
      ]} /></div>
      <Section title="Results">{body}</Section>
    </DetailLayout>
  </>;
}
